<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                {{ editingDisposal ? "Edit Asset Disposal" : "Add New Asset Disposal/Write-off" }}
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-form @submit.prevent="submit" ref="form">
                  <v-row class="pa-1 py-2 px-2">
                    <!-- Disposal Date -->
                    <v-col md="4" cols="12">
                      <v-date-input
                        input-format="dd/mm/yyyy"
                        v-model="disposal_date.value.value"
                        label="Disposal Date *"
                        density="compact"
                        prepend-icon=""
                        prepend-inner-icon="mdi-calendar"
                        variant="outlined"
                        bg-color="grey-lighten-4"
                        :error-messages="disposal_date.errorMessage.value"
                        autocomplete="off"
                        clearable
                      ></v-date-input>
                    </v-col>

                    <!-- Customer (Optional - who receives the asset) -->
                    <v-col md="4" cols="12">
                      <v-autocomplete
                        v-model="customer_id.value.value"
                        label="Customer/Buyer (Optional)"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        :items="getCustomerMapList"
                        :item-title="(item) => item ? `${item.customer.company_name ? item.customer.company_name : item.customer.company_owner_name ? item.customer.company_owner_name : ''} - ${item.customer.code}` : ''"
                        item-value="customer_id"
                        :error-messages="customer_id.errorMessage.value"
                        :loading="customerLoading"
                        prepend-inner-icon="mdi-account"
                        clearable
                      
                          :menu-props="{ contentClass: 'my-custom-dropdown' }"
></v-autocomplete>
                    </v-col>

                    <!-- Reference Number -->
                    <v-col md="4" cols="12">
                      <v-text-field
                        v-model="reference_no.value.value"
                        label="Reference Number (Optional)"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        :error-messages="reference_no.errorMessage.value"
                        prepend-inner-icon="mdi-file-document-outline"
                        clearable
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <!-- Assets Selection Section -->
                  <v-row class="py-2 px-2">
                    <v-col cols="12">
                      <v-card variant="outlined" class="pa-4">
                        <v-card-title class="pa-2">
                          <span class="text-h6">Select Assets for Disposal/Write-off</span>
                        </v-card-title>
                        <v-card-text>
                          <!-- Asset Search -->
                          <v-row class="pb-2">
                            <v-col md="6" cols="12">
                              <v-autocomplete
                                v-model="selectedAssetId"
                                label="Search and Add Asset"
                                variant="outlined"
                                density="compact"
                                bg-color="grey-lighten-4"
                                :items="availableAssets"
                                :item-title="(item) => `${item.code} - ${item.name}`"
                                item-value="id"
                                :loading="assetLoading"
                                return-object
                                prepend-inner-icon="mdi-magnify"
                                clearable
                              
                                  :menu-props="{ contentClass: 'my-custom-dropdown' }"
>
                                <template v-slot:item="{ props, item }">
                                  <v-list-item v-bind="props">
                                    <template v-slot:title>
                                      <div>
                                        <strong>{{ item.raw.code }}</strong> - {{ item.raw.name }}
                                      </div>
                                      <div class="text-caption text-grey">
                                        Category: {{ item.raw.asset_category?.name }} | 
                                        Location: {{ item.raw.asset_location?.name }} | 
                                        Book Value: {{ formatCurrency(item.raw.book_value_start) }}
                                      </div>
                                    </template>
                                  </v-list-item>
                                </template>
                              </v-autocomplete>
                            </v-col>
                            <v-col md="2" cols="12">
                              <v-btn
                                color="primary"
                                block
                                @click="addAssetToDisposal"
                                :disabled="!selectedAssetId"
                                prepend-icon="mdi-plus"
                              >
                                Add Asset
                              </v-btn>
                            </v-col>
                          </v-row>

                          <!-- Selected Assets Table -->
                          <v-data-table
                            :headers="assetHeaders"
                            :items="disposalAssets"
                            :items-per-page="5"
                            density="compact"
                            class="elevation-1"
                          >
                            <template v-slot:item.code="{ item }">
                              <strong>{{ item.code }}</strong>
                            </template>
                            <template v-slot:item.book_value="{ item }">
                              <span class="font-weight-medium">{{ formatCurrency(item.book_value_start) }}</span>
                            </template>
                            <template v-slot:item.sale_value="{ item }">
                              <v-text-field
                                v-model.number="item.sale_value"
                                type="number"
                                step="0.01"
                                min="0"
                                density="compact"
                                variant="outlined"
                                hide-details
                                @update:model-value="calculateGainLoss(item)"
                                style="width: 150px;"
                              ></v-text-field>
                            </template>
                            <template v-slot:item.remarks="{ item }">
                              <v-text-field class="py-2 "
                                v-model="item.remarks"
                                density="compact"
                                variant="outlined"
                                hide-details
                                placeholder="Add remarks..."
                                style="min-width: 200px;"
                              ></v-text-field>
                            </template>
                            <template v-slot:item.actions="{ item }">
                              <v-btn
                                icon="mdi-delete"
                                size="small"
                                color="error"
                                variant="text"
                                @click="removeAssetFromDisposal(item)"
                              ></v-btn>
                            </template>
                            <template v-slot:no-data>
                              <div class="text-center py-4">
                                <v-icon size="48" color="grey">mdi-delete-empty</v-icon>
                                <p class="text-grey">No assets added yet</p>
                              </div>
                            </template>
                            <template v-slot:bottom>
                              <div class="pa-3 text-right">
                                <strong>Total Gain/Loss: </strong>
                                <v-chip 
                                  :color="totalGainLoss >= 0 ? 'success' : 'error'" 
                                  variant="flat"
                                  class="ml-2"
                                >
                                  {{ formatCurrency(totalGainLoss) }}
                                </v-chip>
                              </div>
                            </template>
                          </v-data-table>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>

                  <!-- Action Buttons -->
                  <v-row class="pa-2">
                    <v-col cols="12">
                      <div class="d-flex justify-end" style="gap: 12px;">
                        <v-btn
                          class="bg-grey-darken-1 me-2"
                          variant="flat"
                          type="button"
                          :loading="saving && currentAction === 0"
                          :disabled="saving"
                          @click="submitForm(0)"
                        >
                          DRAFT
                        </v-btn>
                        <v-btn
                          color="primary"
                          type="button"
                          :loading="saving && currentAction === 1"
                          :disabled="saving && currentAction !== 1"
                          
                          @click="submitForm(1)"
                        >
                          {{ editingDisposal ? "Update Disposal" : "POST" }}
                        </v-btn>
                      </div>
                    </v-col>
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import { useStore } from "vuex";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { ref, computed, watch } from "vue";
import { toast } from "vue3-toastify";
import { getAssetRegistrationsList } from "@/store/api";
import Axios from "axios";

export default {
  components: {},
  data() {
    return {
      panel: [0],
      customerLoading: false,
      assetLoading: false,
      saving: false,
      currentAction: null,
      selectedAssetId: null,
      disposalAssets: [],
      allAssets: [],
      currentStatus: 1,
      assetHeaders: [
        { title: "Code", key: "code", align: "left", sortable: false },
        { title: "Name", key: "name", align: "left", sortable: false },
        { title: "Category", key: "asset_category.name", align: "left", sortable: false },
        { title: "Book Value", key: "book_value", align: "right", sortable: false },
        { title: "Sale Value", key: "sale_value", align: "right", sortable: false },
        { title: "Remarks", key: "remarks", align: "left", sortable: false },
        { title: "Actions", key: "actions", align: "center", sortable: false },
      ],
    };
  },
  computed: {
    editingDisposal() {
      return this.$store.getters.getEditingAssetDisposal;
    },
    branchId() {
      return this.$store.getters.getAuthUser.branch_id || null;
    },
    getCustomerMapList() {
      let customers = this.$store.getters.getCustomerMapList;
      return customers.filter(
        (item) => item.branch_id == this.branchId && item.status == 1
      );
    },
    availableAssets() {
      // Filter assets that can be disposed (not already selected)
      return this.allAssets.filter((asset) => {
        // Don't show already selected assets
        return !this.disposalAssets.some((selected) => selected.id === asset.id);
      });
    },
    totalGainLoss() {
      return this.disposalAssets.reduce((sum, asset) => sum + (asset.gain_loss || 0), 0);
    },
  },
  watch: {
    editingDisposal: {
      handler(newVal) {
        if (newVal && newVal.id) {
          // Always fetch fresh data for editing to ensure all relationships are loaded
          this.loadEditDataById(newVal.id);
        }
      },
      immediate: true,
    },
  },
  created() {
    // if (this.$store.getters.getCustomerMapList.length === 0) {
      this.customerLoading = true;
      this.$store.dispatch("getCustomerMapList").then(() => {
        this.customerLoading = false;
      });
    // }
    this.loadAssets();
  },
  setup() {
    const store = useStore();

    const validationSchema = yup.object({
      disposal_date: yup.string().required("Disposal date is required"),
      customer_id: yup.number().nullable(),
      reference_no: yup.string().nullable(),
    });

    const { handleSubmit, resetForm } = useForm({
      validationSchema,
    });

    const disposal_date = useField("disposal_date");
    const customer_id = useField("customer_id");
    const reference_no = useField("reference_no");

    return {
      disposal_date,
      customer_id,
      reference_no,
      handleSubmit,
      resetForm,
    };
  },
  methods: {
    async submitForm(status) {
      this.currentStatus = typeof status !== "undefined" ? status : 1;
      this.currentAction = this.currentStatus;
      try {
        await this.submit();
      } finally {
        this.currentAction = null;
      }
    },
    async submit() {
      try {
        // Check if at least one asset is selected
        if (this.disposalAssets.length === 0) {
          toast.error("Please add at least one asset to dispose");
          return;
        }

        // Validate and submit
        await this.handleSubmit(async (values) => {
          this.saving = true;
          const disposalData = {
            disposal_date: values.disposal_date,
            customer_id: values.customer_id || null,
            reference_no: values.reference_no || null,
            asset_disposal_master_id: this.editingDisposal?.id || null,
            status: this.currentStatus ?? 1,
            assets: this.disposalAssets.map((asset) => ({
              asset_register_id: asset.id,
              sale_value: asset.sale_value || 0,
              remarks: asset.remarks || null,
            })),
          };

          try {
            const response = await this.$store.dispatch("saveUpdateAssetDisposal", disposalData);
            if (response.data.success) {
              toast.success(response.data.message || "Asset disposal saved successfully");
              this.$store.commit("UNSET_EDITING_ASSET_DISPOSAL");
              this.$router.push({ name: "disposal-write-off-records" });
            } else {
              toast.error(response.data.message || "Failed to save asset disposal");
            }
          } catch (error) {
            toast.error(error.response?.data?.message || "Failed to save asset disposal");
          } finally {
            this.saving = false;
          }
        })();
      } catch (error) {
        console.error("Validation error:", error);
      }
    },
    async loadAssets() {
      this.assetLoading = true;
      try {
        const response = await Axios.get(getAssetRegistrationsList, {
          params: {},
        });
        this.allAssets = response.data.data || [];
      } catch (error) {
        console.error("Error loading assets:", error);
        this.allAssets = [];
      } finally {
        this.assetLoading = false;
      }
    },
    async loadEditDataById(id) {
      try {
        // Ensure assets are loaded first
        if (this.allAssets.length === 0) {
          await this.loadAssets();
        }
        
        const response = await this.$store.dispatch("getAssetDisposalById", id);
        console.log("Disposal data response:", response);
        if (response && response.data) {
          this.loadEditData(response.data);
        }
      } catch (error) {
        console.error("Error loading disposal data:", error);
        toast.error("Failed to load disposal data");
      }
    },
    loadEditData(disposal) {
      if (disposal) {
        console.log("Loading edit data:", disposal);
        this.disposal_date.value.value = disposal.disposal_date;
        this.customer_id.value.value = disposal.customer_id;
        this.reference_no.value.value = disposal.reference_no || "";

        // Load existing disposal assets with full details
        if (disposal.details && disposal.details.length > 0) {
          console.log("Disposal details:", disposal.details);
          this.disposalAssets = disposal.details.map((detail) => {
            const asset = detail.assetRegister || this.allAssets.find(a => a.id === detail.asset_register_id);
            if (asset) {
              return {
                ...asset,
                sale_value: detail.sale_value || 0,
                gain_loss: detail.gain_loss || 0,
                remarks: detail.remarks || "",
              };
            }
            return null;
          }).filter(Boolean);
          console.log("Loaded disposal assets:", this.disposalAssets);
        }
      }
    },
    addAssetToDisposal() {
      if (!this.selectedAssetId) {
        toast.error("Please select an asset");
        return;
      }

      // Check if asset is already added
      if (this.disposalAssets.some((asset) => asset.id === this.selectedAssetId.id)) {
        toast.warning("This asset is already added");
        return;
      }

      // Add asset with default values
      const asset = {
        ...this.selectedAssetId,
        sale_value: 0,
        gain_loss: -this.selectedAssetId.book_value_start,
        remarks: "",
      };

      this.disposalAssets.push(asset);
      this.selectedAssetId = null;
      toast.success("Asset added successfully");
    },
    removeAssetFromDisposal(asset) {
      const index = this.disposalAssets.findIndex((a) => a.id === asset.id);
      if (index > -1) {
        this.disposalAssets.splice(index, 1);
        toast.success("Asset removed");
      }
    },
    calculateGainLoss(asset) {
      const saleValue = parseFloat(asset.sale_value) || 0;
      const bookValue = parseFloat(asset.book_value_start) || 0;
      asset.gain_loss = saleValue - bookValue;
    },
    formatCurrency(value) {
      if (!value && value !== 0) return "0.00";
      return parseFloat(value).toFixed(2);
    },
    goBack() {
      this.$store.commit("UNSET_EDITING_ASSET_DISPOSAL");
      this.$router.push({ name: "disposal-write-off-records" });
    },
  },
};
</script>

<style scoped>
.panel-header {
  font-weight: 600;
}
</style>

